import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { AccountEntity } from '@actual-app/core/types/models';

import * as bindings from '#spreadsheet/bindings';

import { useFormat } from './useFormat';
import type { FormatType } from './useFormat';
import { useSyncedPref } from './useSyncedPref';

/** Marks a balance that is an estimate converted at the latest rate. */
export const CONVERTED_BALANCE_PREFIX = '≈ ';

/**
 * How an account's balance should be shown in the sidebar / account list.
 *
 * Foreign-currency accounts show their native balance with that currency's
 * symbol, unless the "sidebarBalancesInBudgetCurrency" preference is on, in
 * which case the balance is converted into the budget currency at the most
 * recently stored exchange rate and marked with "≈".
 */
export function useAccountBalanceDisplay(account: AccountEntity | undefined) {
  const { t } = useTranslation();
  const format = useFormat();
  const [defaultCurrencyCode] = useSyncedPref('defaultCurrencyCode');
  const [convertPref] = useSyncedPref('sidebarBalancesInBudgetCurrency');

  const budgetCurrency = defaultCurrencyCode || '';
  const accountCurrency = account?.currency || '';
  const isForeign =
    accountCurrency !== '' &&
    budgetCurrency !== '' &&
    accountCurrency !== budgetCurrency;
  const isConverted = isForeign && convertPref === 'true';

  const formatter = useCallback(
    (value: unknown, type?: FormatType) =>
      CONVERTED_BALANCE_PREFIX + format(value, type),
    [format],
  );

  if (isConverted && account) {
    return {
      isConverted: true as const,
      currency: undefined,
      convertedBinding: bindings.accountBalanceConverted(account.id),
      formatter,
      title: t('Converted from {{currency}} at the latest exchange rate', {
        currency: accountCurrency,
      }),
    };
  }

  return {
    isConverted: false as const,
    /** currency to format the native balance with */
    currency: isForeign ? accountCurrency : undefined,
    convertedBinding: null,
    formatter: undefined,
    title: undefined,
  };
}
